import { useState } from "react";
import type { BezoekersbestandEntry, EmailLijstKey } from "@domaddress/shared";
import { api } from "../lib/api";
import IsoDateInput from "./IsoDateInput";

interface Props {
  entry: BezoekersbestandEntry | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Form for creating or editing a Bezoekersbestand (visitor register) entry. */
export default function BezoekersFormModal({ entry, onClose, onSaved }: Props) {
  const isEdit = !!entry;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  type LijstKey = Exclude<EmailLijstKey, "geen">;
  const lijstOpts: { value: LijstKey; label: string }[] = [
    { value: "alle_email", label: "Allen met e-mail" },
    { value: "informatief", label: "Informatieve berichten" },
    { value: "overlijden", label: "Bericht bij overlijden" },
    { value: "40_dagen", label: "40-dagen-mailing" },
  ];

  const [form, setForm] = useState({
    first_name: entry?.first_name ?? "",
    last_name: entry?.last_name ?? "",
    tussenvoegsel: entry?.tussenvoegsel ?? "",
    initialen: entry?.initialen ?? "",
    birth_date: entry?.birth_date ?? "",
    death_date: entry?.death_date ?? "",
    kwalificaties: entry?.kwalificaties ?? "",
    email: entry?.email ?? "",
    email_secondary: entry?.email_secondary ?? "",
    dominicus_email: entry?.dominicus_email ?? "",
    phone: entry?.phone ?? "",
    phone_secondary: entry?.phone_secondary ?? "",
    address: entry?.address ?? "",
    city: entry?.city ?? "",
    postal_code: entry?.postal_code ?? "",
    country: entry?.country ?? "",
    notes: entry?.notes ?? "",
    notitie: entry?.notitie ?? "",
    notities: entry?.notities ?? "",
    extra: entry?.extra ?? "",
    bellijn: entry?.bellijn ?? false,
    berichten: entry?.berichten ?? false,
    agv_ingestemd: entry?.agv_ingestemd ?? false,
    email_lijsten: (entry?.email_lijsten ?? []) as LijstKey[],
    wants_newsletter: entry?.wants_newsletter ?? false,
    wants_activities_info: entry?.wants_activities_info ?? false,
    willing_volunteer: entry?.willing_volunteer ?? false,
  });

  function handleFieldChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const t = e.target;
    if (t.type === "checkbox") {
      setForm({ ...form, [t.name]: (t as HTMLInputElement).checked });
    } else {
      setForm({ ...form, [t.name]: t.value });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        email_lijsten: form.email_lijsten.length ? form.email_lijsten : undefined,
      };
      if (isEdit) {
        await api.put(`/api/bezoekersbestand/${entry.id}`, payload);
      } else {
        await api.post("/api/bezoekersbestand", payload);
      }
      onSaved();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? "Bezoeker bewerken" : "Nieuwe bezoeker"}</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label>Voornaam *</label>
              <input name="first_name" required value={form.first_name} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Tussenvoegsel</label>
              <input name="tussenvoegsel" value={form.tussenvoegsel} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Achternaam *</label>
              <input name="last_name" required value={form.last_name} onChange={handleFieldChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Initialen</label>
            <input name="initialen" value={form.initialen} onChange={handleFieldChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Geboortedatum</label>
              <IsoDateInput name="birth_date" value={form.birth_date} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Overlijdensdatum</label>
              <IsoDateInput name="death_date" value={form.death_date} onChange={handleFieldChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Kwalificaties</label>
            <textarea
              name="kwalificaties"
              rows={3}
              value={form.kwalificaties}
              onChange={handleFieldChange}
              placeholder="Bijv. opleiding, titels, certificeringen (meer regels mogelijk)"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>E-mail (primair)</label>
              <input name="email" type="email" value={form.email} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>E-mail (secundair)</label>
              <input
                name="email_secondary"
                type="email"
                value={form.email_secondary}
                onChange={handleFieldChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>E-mail Dominicus (eigen domein)</label>
            <input name="dominicus_email" type="email" value={form.dominicus_email} onChange={handleFieldChange} />
            <span style={{ fontSize: "0.78rem", color: "var(--text-muted, #888)", marginTop: "0.25rem", display: "block" }}>
              Bijvoorbeeld <strong>@dominicusamsterdam.nl</strong> — zelfde veld als bij medewerkerskaarten.
            </span>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Telefoon (primair)</label>
              <input name="phone" type="tel" value={form.phone} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Telefoon (secundair)</label>
              <input name="phone_secondary" type="tel" value={form.phone_secondary} onChange={handleFieldChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Straat en huisnummer</label>
            <input name="address" value={form.address} onChange={handleFieldChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Plaats</label>
              <input name="city" value={form.city} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Postcode</label>
              <input name="postal_code" value={form.postal_code} onChange={handleFieldChange} />
            </div>
            <div className="form-group">
              <label>Land</label>
              <input name="country" value={form.country} onChange={handleFieldChange} placeholder="Nederland" />
            </div>
          </div>

          <div className="form-group">
            <label>Opmerkingen</label>
            <textarea name="notes" rows={3} value={form.notes} onChange={handleFieldChange} />
          </div>
          <div className="form-group">
            <label>Notitie</label>
            <textarea name="notitie" rows={2} value={form.notitie} onChange={handleFieldChange} />
          </div>
          <div className="form-group">
            <label>Notities</label>
            <textarea name="notities" rows={2} value={form.notities} onChange={handleFieldChange} />
          </div>
          <div className="form-group">
            <label>Extra</label>
            <textarea name="extra" rows={2} value={form.extra} onChange={handleFieldChange} />
          </div>

          <div className="form-group">
            <span className="form-group-label" style={{ display: "block", marginBottom: "0.35rem" }}>
              E-mail lijsten (meerdere mogelijk)
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {lijstOpts.map((o) => (
                <label key={o.value} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: "normal" }}>
                  <input
                    type="checkbox"
                    checked={form.email_lijsten.includes(o.value)}
                    onChange={() => {
                      setForm((f) => ({
                        ...f,
                        email_lijsten: f.email_lijsten.includes(o.value)
                          ? f.email_lijsten.filter((x) => x !== o.value)
                          : [...f.email_lijsten, o.value],
                      }));
                    }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-row" style={{ marginBottom: "0.75rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input type="checkbox" name="bellijn" checked={form.bellijn} onChange={handleFieldChange} />
              Bellijn
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input type="checkbox" name="berichten" checked={form.berichten} onChange={handleFieldChange} />
              Berichten
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input type="checkbox" name="agv_ingestemd" checked={form.agv_ingestemd} onChange={handleFieldChange} />
              AVG / voorwaarden ingestemd
            </label>
          </div>

          <div className="detail-section" style={{ marginTop: "1rem" }}>
            <h3 className="section-title" style={{ fontSize: "1rem", marginBottom: "0.75rem" }}>
              Voorkeuren
            </h3>
            <label className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                name="wants_newsletter"
                checked={form.wants_newsletter}
                onChange={handleFieldChange}
              />
              Wil nieuwsbrief ontvangen
            </label>
            <label className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                name="wants_activities_info"
                checked={form.wants_activities_info}
                onChange={handleFieldChange}
              />
              Wil informatie over activiteiten ontvangen
            </label>
            <label className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                name="willing_volunteer"
                checked={form.willing_volunteer}
                onChange={handleFieldChange}
              />
              Bereid om vrijwilligerswerk te doen
            </label>
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuleren
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Opslaan…" : isEdit ? "Opslaan" : "Toevoegen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
